import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Card,
  Input,
  Select,
  DatePicker,
  Button,
  Space,
  Row,
  Col,
  message,
  Spin,
  Popconfirm,
  Typography,
  Modal,
  Table,
  Tag,
  Tooltip,
  Form,
} from 'antd';
import {
  UploadOutlined,
  DeleteOutlined,
  ReloadOutlined,
  PictureOutlined,
  AppstoreOutlined,
  BarsOutlined,
  EditOutlined,
  EyeOutlined,
  DownloadOutlined,
} from '@ant-design/icons';

import mediaService, {
  MediaFile,
  MediaListParams,
  MediaListResponse,
  MediaStats,
  FilterOption
} from '../services/media';
import { MediaGridView, MediaUploader } from '../components/MediaLibrary';
import ColumnSelector from '../components/ColumnSelector';
import type { ColumnsType } from 'antd/es/table';
import useDocumentTitle from '../hooks/useDocumentTitle';
import userPreferenceService from '../services/userPreference';
import ListHelpButton from '../components/ListHelpButton';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Title, Text, Paragraph } = Typography;

const MediaLibraryManagement: React.FC = () => {
  useDocumentTitle('媒体库');
  // 状态管理
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [stats, setStats] = useState<MediaStats['data'] | null>(null);
  const [showUploader, setShowUploader] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [pagination, setPagination] = useState<{ current: number; pageSize: number; total: number }>({
    current: 1,
    pageSize: 50,
    total: 0,
  });
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);

  // 滚动容器引用 & 初始化标记
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const initializedRef = useRef(false);

  // 所有可用的列定义
  const allColumnDefinitions: Record<string, any> = {
    title: {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      width: 200,
      fixed: 'left',
      render: (title: string, record: MediaFile) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {record.file_type === 'image' && (
            <img
              src={mediaService.getMediaUrl(record)}
              alt={title}
              style={{ width: 32, height: 32, objectFit: 'cover', marginRight: 8, borderRadius: 4 }}
            />
          )}
          <span>{title}</span>
        </div>
      ),
    },
    original_name: {
      title: '原始文件名',
      dataIndex: 'original_name',
      key: 'original_name',
      width: 200,
      ellipsis: true,
    },
    file_type: {
      title: '文件类型',
      dataIndex: 'file_type',
      key: 'file_type',
      width: 100,
      render: (type: string) => {
        const colors: Record<string, string> = {
          image: 'green',
          video: 'blue',
          audio: 'orange',
          document: 'purple',
          other: 'default',
        };
        return <Tag color={colors[type] || 'default'}>{type.toUpperCase()}</Tag>;
      },
    },
    mime_type: {
      title: 'MIME类型',
      dataIndex: 'mime_type',
      key: 'mime_type',
      width: 120,
    },
    file_size: {
      title: '文件大小',
      dataIndex: 'file_size',
      key: 'file_size',
      width: 100,
      render: (size: number) => mediaService.formatFileSize(size),
      sorter: (a: MediaFile, b: MediaFile) => a.file_size - b.file_size,
    },
    dimensions: {
      title: '尺寸',
      dataIndex: 'dimensions',
      key: 'dimensions',
      width: 120,
      render: (dimensions: any) =>
        dimensions ? `${dimensions.width} × ${dimensions.height}` : '-',
    },
    upload_date: {
      title: '上传时间',
      dataIndex: 'upload_date',
      key: 'upload_date',
      width: 150,
      render: (date: string) => new Date(date).toLocaleString(),
      sorter: (a: MediaFile, b: MediaFile) => new Date(a.upload_date).getTime() - new Date(b.upload_date).getTime(),
    },
    alt_text: {
      title: '替代文本',
      dataIndex: 'alt_text',
      key: 'alt_text',
      width: 150,
      ellipsis: true,
    },
    description: {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      width: 200,
      ellipsis: true,
    },
    actions: {
      title: '操作',
      key: 'actions',
      fixed: 'right',
      width: 150,
      render: (_: any, record: MediaFile) => (
        <Space>
          <Tooltip title="预览">
            <Button
              icon={<EyeOutlined />}
              onClick={() => handlePreview(record)}
              size="small"
            />
          </Tooltip>
          <Tooltip title="下载">
            <Button
              icon={<DownloadOutlined />}
              onClick={() => mediaService.downloadMedia(record.id, record.original_name)}
              size="small"
            />
          </Tooltip>
          <Tooltip title="编辑">
            <Button
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              size="small"
            />
          </Tooltip>
          <Tooltip title="删除">
            <Button
              icon={<DeleteOutlined />}
              onClick={() => handleMediaDelete(record.id)}
              size="small"
              danger
            />
          </Tooltip>
        </Space>
      ),
    },
  };

  // 默认显示的列
  const defaultVisibleColumns = [
    'title',
    'file_type',
    'file_size',
    'upload_date',
    'actions'
  ];

  // 初始化可见列
  useEffect(() => {
    (async () => {
      // 视图模式偏好
      try {
        const pref = await userPreferenceService.getPreference('media_library_view_mode');
        if (pref.success && pref.data?.value && (pref.data.value === 'grid' || pref.data.value === 'table')) {
          setViewMode(pref.data.value);
        }
      } catch {}

      // 每页条数偏好（仅表格视图使用，但统一作为 per_page 来源）
      try {
        const savedPageSize = await userPreferenceService.getPageSizeSettings('mediaLibrary');
        const pageSizeToUse = savedPageSize && savedPageSize > 0 ? savedPageSize : (searchParams.per_page || 50);
        const initialParams = { ...searchParams, per_page: pageSizeToUse, page: 1 } as MediaListParams;
        setSearchParams(initialParams);
        setPagination(prev => ({ ...prev, pageSize: pageSizeToUse, current: 1 }));
        // 首次加载：使用应用了偏好的参数
        await loadMediaList(initialParams);
      } catch {}

      // 列显示默认
      if (visibleColumns.length === 0) {
        setVisibleColumns(defaultVisibleColumns);
      }

      // 加载筛选选项与统计
      await loadStats();
      await fetchFilterOptions();
      initializedRef.current = true;
    })();
  }, []);

  // 生成表格列
  const columns: ColumnsType<MediaFile> = visibleColumns
    .map(columnKey => allColumnDefinitions[columnKey])
    .filter(Boolean);

  // 获取可用列配置
  const availableColumns = Object.keys(allColumnDefinitions).map(key => ({
    key,
    title: allColumnDefinitions[key].title,
    fixed: allColumnDefinitions[key].fixed
  }));

  // 处理列显示变更
  const handleColumnChange = (checkedColumns: string[]) => {
    // 按默认顺序排序，确保重新显示的列回到原位置；actions 固定在末尾
    const withoutActions = checkedColumns.filter(k => k !== 'actions');
    const indexOfInBase = (key: string) => {
      const i = defaultVisibleColumns.indexOf(key);
      return i >= 0 ? i : Number.MAX_SAFE_INTEGER / 2;
    };
    const sorted = withoutActions.slice().sort((a, b) => indexOfInBase(a) - indexOfInBase(b));
    setVisibleColumns([...sorted, 'actions']);
  };

  // 搜索和筛选
  const [searchParams, setSearchParams] = useState<MediaListParams>({
    page: 1,
    per_page: 50, // 默认50张图片
    sort_by: 'upload_date',
    sort_order: 'desc',
  });

  // 筛选状态
  const [filters, setFilters] = useState({
    file_type: '',
  });

  // 动态筛选选项
  const [filterOptions, setFilterOptions] = useState({
    file_types: [] as FilterOption[],
  });

  // 获取筛选选项
  const fetchFilterOptions = async () => {
    try {
      const response = await mediaService.getFilterOptions();
      if (response.success) {
        setFilterOptions(response.data);
      }
    } catch (error) {
      console.error('获取筛选选项失败:', error);
    }
  };

  // 处理筛选变化
  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    // 重置到第一页并重新加载
    const newSearchParams = {
      ...searchParams,
      page: 1,
      [key]: value || undefined,
    };
    setSearchParams(newSearchParams);
    loadMediaList(newSearchParams);
  };

  // 重置筛选
  const handleResetFilters = () => {
    setFilters({
      file_type: '',
    });
    const newSearchParams = {
      ...searchParams,
      page: 1,
      file_type: undefined,
      search: undefined,
    };
    setSearchParams(newSearchParams);
    loadMediaList(newSearchParams);
  };

  // 加载媒体列表
  const loadMediaList = useCallback(async (params: MediaListParams = searchParams, append = false) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const response: MediaListResponse = await mediaService.getMediaList(params);
      if (response.success) {
        const filteredData = response.data.data;

        if (append) {
          // 追加数据
          setMediaFiles(prev => [...prev, ...filteredData]);
        } else {
          // 替换数据
          setMediaFiles(filteredData);
        }

        // 更新分页信息与是否还有更多
        setHasMore(response.data.current_page < response.data.last_page);
        setPagination({
          current: response.data.current_page,
          pageSize: response.data.per_page,
          total: response.data.total,
        });
      }
    } catch (error: any) {
      console.error('Failed to load media list:', error);
      message.error('加载媒体列表失败');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [searchParams]);

  // 加载更多数据
  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore) return;

    const nextParams = {
      ...searchParams,
      page: Math.floor(mediaFiles.length / searchParams.per_page!) + 1,
    };

    loadMediaList(nextParams, true);
  }, [searchParams, mediaFiles.length, loadingMore, hasMore, loadMediaList]);

  // 滚动处理
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

    // 距离底部100px时开始加载
    if (scrollHeight - scrollTop - clientHeight < 100) {
      loadMore();
    }
  }, [loadMore]);

  // 加载统计信息
  const loadStats = async () => {
    try {
      const response = await mediaService.getStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  // 初始化加载（已由偏好加载完成后执行，此处仅作为兜底）
  useEffect(() => {
    if (!initializedRef.current) return;
    // 如果上面的初始化未成功，这里兜底再加载一次
    if (mediaFiles.length === 0) {
      loadMediaList({ ...searchParams, page: 1 });
      loadStats();
      fetchFilterOptions();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 搜索参数变化时重新加载
  useEffect(() => {
    const resetParams = { ...searchParams, page: 1 };
    setSearchParams(resetParams);
    loadMediaList(resetParams);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.search, searchParams.file_type, searchParams.date_from, searchParams.date_to, searchParams.sort_by, searchParams.sort_order]);

  // 处理搜索
  const handleSearch = (value: string) => {
    setSearchParams(prev => ({
      ...prev,
      search: value || undefined,
      page: 1,
    }));
  };

  // 处理文件类型筛选
  const handleFileTypeChange = (value: string) => {
    setSearchParams(prev => ({
      ...prev,
      file_type: value || undefined,
      page: 1,
    }));
  };

  // 处理日期范围筛选
  const handleDateRangeChange = (dates: any) => {
    setSearchParams(prev => ({
      ...prev,
      date_from: dates?.[0]?.format('YYYY-MM-DD'),
      date_to: dates?.[1]?.format('YYYY-MM-DD'),
      page: 1,
    }));
  };

  // 处理选择变化
  const handleSelectionChange = (ids: number[]) => {
    setSelectedIds(ids);
  };

  // 处理上传成功
  const handleUploadSuccess = (mediaFile: MediaFile) => {
    setMediaFiles(prev => [mediaFile, ...prev]);
    loadStats(); // 更新统计信息
    setShowUploader(false); // 关闭上传器
  };

  // 处理媒体更新
  const handleMediaUpdate = (updatedMedia: MediaFile) => {
    setMediaFiles(prev =>
      prev.map(media =>
        media.id === updatedMedia.id ? updatedMedia : media
      )
    );
  };

  // 处理媒体删除
  const handleMediaDelete = async (id: number) => {
    try {
      await mediaService.deleteMedia(id);
      setMediaFiles(prev => prev.filter(media => media.id !== id));
      setSelectedIds(prev => prev.filter(selectedId => selectedId !== id));
      message.success('删除成功');
      loadStats(); // 更新统计信息
    } catch (error: any) {
      message.error(error.message || '删除失败');
    }
  };

  // 处理批量删除
  const handleBatchDelete = async () => {
    if (selectedIds.length === 0) {
      message.warning('请先选择要删除的文件');
      return;
    }

    try {
      await mediaService.batchDelete(selectedIds);
      setMediaFiles(prev =>
        prev.filter(media => !selectedIds.includes(media.id))
      );
      setSelectedIds([]);
      message.success(`成功删除 ${selectedIds.length} 个文件`);
      loadStats(); // 更新统计信息
    } catch (error: any) {
      message.error(error.message || '批量删除失败');
    }
  };

  // 重置筛选
  const handleReset = () => {
    setSearchParams({
      page: 1,
      per_page: 50,
      sort_by: 'upload_date',
      sort_order: 'desc',
    });
  };

  // 处理预览
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewMedia, setPreviewMedia] = useState<MediaFile | null>(null);

  const handlePreview = (media: MediaFile) => {
    setPreviewMedia(media);
    setPreviewVisible(true);
  };



  // 处理编辑
  const [editVisible, setEditVisible] = useState(false);
  const [editingMedia, setEditingMedia] = useState<MediaFile | null>(null);
  const [editForm] = Form.useForm();

  const handleEdit = (media: MediaFile) => {
    setEditingMedia(media);
    editForm.setFieldsValue({
      title: media.title,
      alt_text: media.alt_text,
      description: media.description,
    });
    setEditVisible(true);
  };

  const handleEditSubmit = async (values: any) => {
    if (!editingMedia) return;

    try {
      const response = await mediaService.updateMedia(editingMedia.id, values);
      if (response.success && response.data) {
        message.success('媒体信息更新成功');
        handleMediaUpdate(response.data);
        setEditVisible(false);
        setEditingMedia(null);
        editForm.resetFields();
      }
    } catch (error: any) {
      message.error(error.message || '更新失败');
    }
  };

  return (
    <div style={{ padding: 24 }}>
      {/* 操作工具栏 */}
      <Card
        size="small"
        style={{ marginBottom: 16 }}
        title={(
          <Space size="small">
            <span>媒体库</span>
            <ListHelpButton page_key="media_library" />
          </Space>
        )}
      >
        <Row gutter={[16, 16]} align="middle">
          {/* 左侧筛选表单 */}
          <Col span={16}>
            <Space>
              <Input.Search
                placeholder="搜索文件名、标题..."
                allowClear
                onSearch={handleSearch}
                style={{ width: 300 }}
              />
              <Select
                placeholder="文件类型"
                allowClear
                showSearch
                style={{ width: 120 }}
                onChange={handleFileTypeChange}
                value={searchParams.file_type}
              >
                <Option value="image">图片</Option>
                <Option value="document">文档</Option>
                <Option value="video">视频</Option>
                <Option value="audio">音频</Option>
                <Option value="other">其他</Option>
              </Select>
              <RangePicker
                placeholder={['开始日期', '结束日期']}
                style={{ width: 240 }}
                onChange={handleDateRangeChange}
              />
            </Space>
          </Col>

          {/* 右侧按钮组 */}
          <Col span={8} style={{ textAlign: 'right' }}>
            <Space>
              <Space.Compact>
                <Button
                  type={viewMode === 'grid' ? 'primary' : 'default'}
                  icon={<AppstoreOutlined />}
                  onClick={async () => {
                    setViewMode('grid');
                    try { await userPreferenceService.setPreference('media_library_view_mode', 'grid'); } catch {}
                  }}
                  title="网格视图"
                />
                <Button
                  type={viewMode === 'table' ? 'primary' : 'default'}
                  icon={<BarsOutlined />}
                  onClick={async () => {
                    setViewMode('table');
                    try { await userPreferenceService.setPreference('media_library_view_mode', 'table'); } catch {}
                  }}
                  title="表格视图"
                />
              </Space.Compact>

              {viewMode === 'table' && (
                <ColumnSelector
                  availableColumns={availableColumns}
                  visibleColumns={visibleColumns}
                  onColumnChange={handleColumnChange}
                  fixedColumns={['title', 'actions']}
                />
              )}

              <Button
                type="primary"
                icon={<UploadOutlined />}
                onClick={() => setShowUploader(true)}
              >
                上传文件
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => loadMediaList()}
              >
                刷新
              </Button>
              <Button onClick={handleReset}>
                重置
              </Button>
              {selectedIds.length > 0 && (
                <Popconfirm
                  title="确定要删除选中的文件吗？"
                  onConfirm={handleBatchDelete}
                >
                  <Button danger icon={<DeleteOutlined />}>
                    删除选中 ({selectedIds.length})
                  </Button>
                </Popconfirm>
              )}
            </Space>
          </Col>
        </Row>


        {/* 筛选状态显示 */}
        {Object.values(filters).some(value => value) && (
          <Row style={{ marginTop: 16 }}>
            <Col span={24}>
              <div style={{
                padding: '8px 12px',
                backgroundColor: '#f0f2f5',
                borderRadius: '6px',
                fontSize: '14px',
                color: '#666'
              }}>
                <Space wrap>
                  <span>已应用筛选条件：</span>
                  {filters.file_type && (
                    <Tag closable onClose={() => handleFilterChange('file_type', '')}>
                      文件类型: {filters.file_type.toUpperCase()}
                    </Tag>
                  )}
                  <Button type="link" size="small" onClick={handleResetFilters}>
                    清除所有筛选
                  </Button>
                </Space>
              </div>
            </Col>
          </Row>
        )}
      </Card>

      {/* 媒体视图 - 支持网格/表格切换 */}
      <Card>
        {viewMode === 'grid' ? (
          <div
            ref={scrollContainerRef}
            style={{
              height: 'calc(100vh - 300px)', // 自适应高度
              minHeight: 600,
              overflowY: 'auto',
              padding: '0 8px',
            }}
            onScroll={handleScroll}
          >
            <MediaGridView
              mediaFiles={mediaFiles}
              loading={loading}
              selectable={true}
              selectedIds={selectedIds}
              onSelectionChange={handleSelectionChange}
              onMediaUpdate={handleMediaUpdate}
              onMediaDelete={handleMediaDelete}
            />

            {/* 加载更多指示器 */}
            {loadingMore && (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <Spin /> <Text type="secondary">加载更多...</Text>
              </div>
            )}

            {/* 没有更多数据提示 */}
            {!hasMore && mediaFiles.length > 0 && (
              <div style={{ textAlign: 'center', padding: '20px 0', color: '#999' }}>
                <Text type="secondary">已显示全部 {mediaFiles.length} 个文件</Text>
              </div>
            )}

            {/* 空状态 */}
            {!loading && mediaFiles.length === 0 && (
              <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                <PictureOutlined style={{ fontSize: 64, color: '#d9d9d9' }} />
                <Title level={4} type="secondary">
                  暂无媒体文件
                </Title>
                <Paragraph type="secondary">
                  点击右上角的「上传文件」按钮开始添加媒体文件
                </Paragraph>
                <Button type="primary" icon={<UploadOutlined />} onClick={() => setShowUploader(true)}>
                  上传第一个文件
                </Button>
              </div>
            )}
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={mediaFiles}
            loading={loading}
            scroll={{ x: 1200 }}
            rowSelection={{
              selectedRowKeys: selectedIds,
              onChange: (selectedRowKeys) => {
                setSelectedIds(selectedRowKeys as number[]);
              },
            }}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
              onChange: async (page, pageSize) => {
                const newParams = {
                  ...searchParams,
                  page,
                  per_page: pageSize,
                };
                setSearchParams(newParams);
                loadMediaList(newParams);
                try { if (pageSize && pageSize !== pagination.pageSize) await userPreferenceService.savePageSizeSettings('mediaLibrary', pageSize); } catch {}
              },
            }}
            rowKey="id"
          />
        )}
      </Card>

      {/* 上传器模态框 */}
      <Modal
        title="上传文件"
        open={showUploader}
        onCancel={() => setShowUploader(false)}
        footer={null}
        width={600}
      >
        <MediaUploader
          onUploadSuccess={handleUploadSuccess}
          onUploadError={(error) => {
            console.error('Upload error:', error);
            message.error('上传失败');
          }}
          multiple
          maxSize={50}
        />
      </Modal>

      {/* 预览模态框 */}
      <Modal
        title="媒体预览"
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        footer={null}
        width="600px"
        centered
      >
        {previewMedia && (
          <div>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              {previewMedia.file_type === 'image' ? (
                <img
                  src={mediaService.getMediaUrl(previewMedia)}
                  alt={previewMedia.alt_text || previewMedia.title}
                  style={{ maxWidth: '100%', maxHeight: '60vh' }}
                />
              ) : previewMedia.file_type === 'video' ? (
                <video
                  src={mediaService.getMediaUrl(previewMedia)}
                  controls
                  style={{ maxWidth: '100%', maxHeight: '60vh' }}
                />
              ) : previewMedia.file_type === 'audio' ? (
                <audio
                  src={mediaService.getMediaUrl(previewMedia)}
                  controls
                  style={{ width: '100%' }}
                />
              ) : (
                <div style={{ padding: 48, textAlign: 'center' }}>
                  <PictureOutlined style={{ fontSize: 64, color: '#666' }} />
                  <div style={{ marginTop: 16 }}>
                    <Text>无法预览此文件类型</Text>
                  </div>
                </div>
              )}
            </div>

            <div>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <div><strong>文件名:</strong> {previewMedia.title}</div>
                <div><strong>原始名称:</strong> {previewMedia.original_name}</div>
                <div><strong>文件类型:</strong> {previewMedia.mime_type}</div>
                <div><strong>文件大小:</strong> {mediaService.formatFileSize(previewMedia.file_size)}</div>
                {previewMedia.dimensions && (
                  <div><strong>尺寸:</strong> {previewMedia.dimensions.width} × {previewMedia.dimensions.height}</div>
                )}
                <div><strong>上传时间:</strong> {new Date(previewMedia.upload_date).toLocaleString()}</div>
                {previewMedia.alt_text && (
                  <div><strong>替代文本:</strong> {previewMedia.alt_text}</div>
                )}
                {previewMedia.description && (
                  <div><strong>描述:</strong> {previewMedia.description}</div>
                )}
              </Space>
            </div>
          </div>
        )}
      </Modal>

      {/* 编辑模态框 */}
      <Modal
        title="编辑媒体信息"
        open={editVisible}
        onCancel={() => {
          setEditVisible(false);
          setEditingMedia(null);
          editForm.resetFields();
        }}
        onOk={() => editForm.submit()}
        confirmLoading={false}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleEditSubmit}
        >
          <Form.Item
            label="标题"
            name="title"
            rules={[{ required: true, message: '请输入标题' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="替代文本"
            name="alt_text"
          >
            <Input placeholder="用于图片的替代文本" />
          </Form.Item>

          <Form.Item
            label="描述"
            name="description"
          >
            <Input.TextArea rows={3} placeholder="文件描述" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default MediaLibraryManagement;
